import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { login, authenticateWithGoogle } from '../../api/auth';
import { acceptGroupInvitation, rejectGroupInvitation } from '../../api/group';
import Logo from '../logo/Logo';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { ArrowRightIcon } from '@radix-ui/react-icons';
import { useToast } from "../../hooks/useToast";
import GoogleSignInButton from '../common/GoogleSignInButton';
import PrivacyLink from '../common/PrivacyLink'

const LoginBox = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get('groupId');
  const action = searchParams.get('action'); // 'accept' or 'reject'
  const { toast } = useToast();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If there's a group invitation, show a toast to inform the user
    if (groupId && action) {
      toast({
        title: "Group Invitation",
        description: "Login to respond to the group invitation",
        type: "info"
      });
    }
  }, [groupId, action, toast]);

  const handleGroupInvitation = async () => {
    try {
      if (action === 'accept') {
        await acceptGroupInvitation(groupId);
        toast({
          title: "Success",
          description: "You have joined the group successfully!",
          type: "success"
        });
        navigate(`/group/${groupId}/view`);
      } else if (action === 'reject') {
        await rejectGroupInvitation(groupId);
        toast({
          title: "Success",
          description: "You have declined the group invitation",
          type: "success"
        });
        navigate('/groups');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to process group invitation",
        type: "error"
      });
      navigate('/groups');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(formData.email, formData.password);
      
      if (groupId && action) {
        // If this login came from a group invitation, handle it
        await handleGroupInvitation();
      } else {
        navigate('/home');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to login',
        type: "error",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credential) => {
    try {
      await authenticateWithGoogle(credential);
      
      if (groupId && action) {
        // If this login came from a group invitation, handle it
        await handleGroupInvitation();
      } else {
        navigate('/home');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to sign in with Google",
        type: "error"
      });
    }
  };

  const handleGoogleError = (error) => {
    toast({
      title: "Error",
      description: error || "Failed to sign in with Google",
      type: "error"
    });
  };

  return (
    <motion.div 
      className="min-h-screen grid lg:grid-cols-2 bg-gradient-to-br from-primary/5 via-background to-secondary/5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Left side - Branding */}
      <div className="hidden lg:flex flex-col items-center justify-center p-8 relative overflow-hidden">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="relative z-10"
        >
          <div className="flex justify-center">
            <Logo />
          </div>
          <p className="mt-6 text-lg text-center text-muted-foreground max-w-sm">
            "For I am not Eternity, but a human being;<br/>a part of the whole, as an hour is part of the day. I must come like the hour, and like the hour must pass!"
          </p>
        </motion.div>
        
        {/* Background decoration */}
        <div className="absolute inset-0 z-0">
          <motion.div
            className="absolute inset-0 bg-primary/5 backdrop-blur-3xl"
            // initial={{ opacity: 0, scale: 0.8 }}
            // animate={{ opacity: 1, scale: 1 }}
            // transition={{ delay: 0.3, duration: 0.8 }}
          />
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-primary/10 backdrop-blur-3xl"
              style={{
                width: '30vw',
                height: '30vw',
                top: ['20%', '50%', '70%'][i],
                left: ['20%', '60%', '30%'][i],
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 + (i * 0.2), duration: 1 }}
            />
          ))}
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex items-center justify-center p-8">
        <motion.div
          className="w-full max-w-md space-y-8"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <div className="lg:hidden mb-8 flex justify-center">
            <Logo />
          </div>

          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Welcome back</h1>
            <p className="text-sm text-muted-foreground">
              Enter your credentials to access your account
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full"
                />
                <div className="flex justify-end">
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-primary hover:text-primary/90 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full button-glow"
              disabled={loading}
            >
              {loading ? (
                <motion.div
                  className="h-5 w-5 border-2 border-current border-r-transparent rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
              ) : (
                <>
                  Sign in
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 px-2 text-muted-foreground">
                  or continue with
                </span>
              </div>
            </div>

            <GoogleSignInButton
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
            />
          </form>

          <div className="text-center text-sm">
            <span className="text-muted-foreground">Don't have an account? </span>
            <Link 
              to="/signup" 
              className="font-medium text-primary hover:text-primary/90 transition-colors"
            >
              Sign up
            </Link>
          </div>

          <PrivacyLink />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default LoginBox; 